import { Link } from 'react-router-dom'
import { useRelatedDocuments } from '@/hooks/useDocuments'
import { StatusBadge } from '@/components/common'
import type { Document } from '@/types'
import { getDocumentTypeLabel } from '@/types'

interface RelatedDocumentsProps {
  documentId: string
  // Optional callback fired when a related document is opened. Navigation itself
  // happens via the document_number link (a real /documents/:id URL).
  onDocumentSelect?: (id: string) => void
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function RelatedDocuments({ documentId, onDocumentSelect }: RelatedDocumentsProps) {
  const { data: relatedDocs, isLoading, error } = useRelatedDocuments(documentId)

  if (isLoading) {
    return (
      <div className="animate-pulse" data-testid="related-loading">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-sm text-gray-500" data-testid="related-error">
        No related documents found
      </div>
    )
  }

  if (!relatedDocs || relatedDocs.length === 0) {
    return (
      <div className="text-sm text-gray-500" data-testid="related-empty">
        No related documents
      </div>
    )
  }

  return (
    <div className="space-y-2" data-testid="related-documents">
      <h3 className="text-sm font-medium text-gray-700">Related Documents</h3>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Number
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {relatedDocs.map((doc: Document) => (
              <tr key={doc.id} data-testid={`related-doc-${doc.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Link
                    to={`/documents/${doc.id}`}
                    onClick={() => onDocumentSelect?.(doc.id)}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    data-testid={`related-doc-link-${doc.id}`}
                  >
                    {doc.document_number || 'No number'}
                  </Link>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                  {formatDate(doc.document_date || doc.created_at)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                  {getDocumentTypeLabel(doc.document_type, doc.document_type_id)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <StatusBadge status={doc.document_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
